/* eslint-disable @typescript-eslint/no-use-before-define */
import { StackLayout } from '@euler/app/components/StackLayout';
import { ProductItemCell } from '@euler/app/flows/products/components/ProductItemCell';
import { MediaInfo } from '@euler/app/flows/task/functions/models';
import { RelatedFaultLikeSubjectsToSolve } from '@euler/app/flows/task/pages/inspection/site-inspection/components/ItemInspectionView/RelatedFaultLikeSubjectsToSolve';
import { isSimpleNumericInspectionResultDataType } from '@euler/functions/inspectionResultDataTypeHelpers';
import { deriveManifestItemAdopter } from '@euler/functions/product/deriveManifestItemAdopter';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { DefectiveLevel } from '@euler/model';
import { TaskProductManifestItemStaged } from '@euler/model/task-detail';
import { memo, useCallback, useMemo } from 'react';
import { StyleProp, View, ViewStyle } from 'react-native';
import { useTheme } from 'styled-components';
import { ItemMediaList } from '../../../../../components/ItemMediaList';
import * as SiteInspection from '../../functions/SiteInspection';
import { ItemHeaderCell } from './ItemHeaderCell';
import { ItemOptionCell } from './ItemOptionCell';
import {
  MaintenanceAdviceCell,
  kMaintenanceAdviceFallbackText,
} from './MaintenanceAdviceCell';
import { ValueInputCell } from './ValueInputCell';

type CheckItem = SiteInspection.CheckItemInfo;
type Media = MediaInfo;

export type ItemInspectionCallbackProps = {
  onValueChange?: (item: CheckItem, value: number | undefined) => void;
  onSelectedOptionChange?: (item: CheckItem, index: number) => void;
  onDelete?: (item: CheckItem) => void;
  onTakeMedia?: (item: CheckItem) => void;
  onDeleteMedia?: (item: CheckItem, image: Media) => void;
  onEditMedia?: (item: CheckItem, image: Media) => void;
  onRetryMediaUpload?: (item: CheckItem, image: Media) => void;
  onEditMaintenanceAdvice?: (item: CheckItem) => void;
  onPreviewVideo?: (item: CheckItem, image: Media) => void;
  onAssociateProduct?: (subject: string, item: CheckItem) => void;
  onRemoveProductManifestItem?: (item: TaskProductManifestItemStaged) => void;
  onPressProductManifestItem?: (item: TaskProductManifestItemStaged) => void;
  onToggleRelatedFaultLikeSubject?: (item: CheckItem, name: string) => void;
};

type Props = {
  // input props
  itemNo: number;
  item: CheckItem;
  canAssociateProduct: boolean;
  productManifestItems: TaskProductManifestItemStaged[];
  style?: StyleProp<ViewStyle>;
} & ItemInspectionCallbackProps;

export const ItemInspectionView = memo(
  ({
    itemNo,
    item,
    style,
    productManifestItems,
    canAssociateProduct,
    onValueChange,
    onDelete,
    onSelectedOptionChange,
    onEditMaintenanceAdvice,
    onTakeMedia,
    onDeleteMedia,
    onEditMedia,
    onPreviewVideo,
    onRetryMediaUpload,
    onAssociateProduct,
    onRemoveProductManifestItem,
    onPressProductManifestItem,
    onToggleRelatedFaultLikeSubject,
  }: Props) => {
    const {
      inspection: {
        siteInspection: {
          itemCard: { bgColor, shadowColor },
        },
      },
    } = useTheme();
    const { rpx } = useResponsiveContext();
    const selectedOption = useMemo(
      () => item.options[item.selectedOptionIndex],
      [item.options, item.selectedOptionIndex],
    );

    const isNumericType = isSimpleNumericInspectionResultDataType(
      item.valueType,
    );

    const onOptionSelected = useCallback(
      (index: number) => {
        onSelectedOptionChange?.(item, index);
      },
      [item, onSelectedOptionChange],
    );

    const onDeletePress = useCallback(() => {
      onDelete?.(item);
    }, [item, onDelete]);

    const onEditMaintenancePress = useCallback(() => {
      onEditMaintenanceAdvice?.(item);
    }, [item, onEditMaintenanceAdvice]);

    const onAssociateProductPress = useCallback(
      (subject: string) => {
        onAssociateProduct?.(subject, item);
      },
      [item, onAssociateProduct],
    );

    const onValueInputChange = useCallback(
      (newValue: number | undefined) => {
        onValueChange?.(item, newValue);
      },
      [item, onValueChange],
    );

    const handleTakeMedia = useCallback(() => {
      onTakeMedia?.(item);
    }, [item, onTakeMedia]);

    const handleDeleteMedia = useCallback(
      (media: Media) => {
        onDeleteMedia?.(item, media);
      },
      [item, onDeleteMedia],
    );

    const handleEditMedia = useCallback(
      (media: Media) => {
        onEditMedia?.(item, media);
      },
      [onEditMedia, item],
    );

    const handleRetryMediaUpload = useCallback(
      (media: Media) => {
        onRetryMediaUpload?.(item, media);
      },
      [onRetryMediaUpload, item],
    );

    const handlePreviewVideo = useCallback(
      (media: Media) => {
        onPreviewVideo?.(item, media);
      },
      [onPreviewVideo, item],
    );

    const handleToggleRelatedFaultLikeSubject = useCallback(
      (name: string) => {
        onToggleRelatedFaultLikeSubject?.(item, name);
      },
      [item, onToggleRelatedFaultLikeSubject],
    );

    return (
      <View
        css={`
          background-color: ${bgColor};
          //box-shadow: 2px 2px 10px ${shadowColor};
          elevation: 2;
          padding: ${rpx(15)}px;
        `}
        style={style}
      >
        {/** header */}
        <ItemHeaderCell
          title={item.name}
          itemId={item.id}
          itemNo={itemNo}
          canDelete={item.type === 'customIssue'}
          onDeletePress={onDeletePress}
          css={`
            margin-bottom: ${rpx(10)}px;
          `}
        />

        {/** value input */}
        {isNumericType && (
          <ValueInputCell
            value={item.value}
            onChange={onValueInputChange}
            valueUnit={item.valueUnit}
            css={`
              margin-bottom: ${rpx(10)}px;
            `}
          />
        )}

        {/** options */}
        {item.options.map((option, i) => (
          <ItemOptionCell
            key={i}
            title={option.title}
            index={i}
            defectiveLevel={option.defectiveLevel}
            selected={item.selectedOptionIndex === i}
            onSelected={onOptionSelected}
            css={`
              margin-bottom: ${rpx(10)}px;
            `}
          />
        ))}

        {/** maintenance advice */}
        {selectedOption &&
        selectedOption.defectiveLevel !== DefectiveLevel.Fine ? (
          <MaintenanceAdviceCell
            canAssociateProduct={canAssociateProduct}
            text={
              selectedOption.maintenanceAdvice ?? kMaintenanceAdviceFallbackText
            }
            onEditPress={onEditMaintenancePress}
            onAssociateProductPress={onAssociateProductPress}
            css={`
              margin-top: ${rpx(10)}px;
              margin-bottom: ${rpx(20)}px;
            `}
          />
        ) : null}

        {item.relatedFaultLikeSubjects?.length ? (
          <RelatedFaultLikeSubjectsToSolve
            subjects={item.relatedFaultLikeSubjects}
            onToggle={handleToggleRelatedFaultLikeSubject}
            css={`
              margin-top: ${rpx(
                selectedOption &&
                  selectedOption.defectiveLevel !== DefectiveLevel.Fine
                  ? 0
                  : 10,
              )}px;
              margin-bottom: ${rpx(20)}px;
            `}
          />
        ) : null}

        {productManifestItems.length > 0 &&
          selectedOption != null &&
          selectedOption.defectiveLevel !== DefectiveLevel.Fine && (
            <StackLayout
              direction="column"
              alignItems="stretch"
              gap={rpx(8)}
              css={`
                margin-top: ${rpx(-10)}px;
                margin-bottom: ${rpx(10)}px;
              `}
            >
              {productManifestItems
                .filter(
                  x =>
                    x.subject ===
                    (selectedOption.maintenanceAdvice ??
                      kMaintenanceAdviceFallbackText),
                )
                .map(x => (
                  <ProductItemCell
                    key={`${x.subject}/${x.product.id}`}
                    item={x}
                    name={x.product.name}
                    qty={x.qty}
                    hideQty
                    title={x.product.title}
                    coverUrl={x.product.coverUrl}
                    removable={
                      !x.jobRefs?.length &&
                      (x.confirmations == null ||
                        !Object.values(x.confirmations).at(0)?.adopted)
                    }
                    adopted={
                      x.jobRefs?.length
                        ? true
                        : x.confirmations
                        ? Object.values(x.confirmations).at(0)?.adopted
                        : undefined
                    }
                    showBadge={true}
                    author={x.author}
                    adopter={deriveManifestItemAdopter(x)}
                    closer={x.closer ?? undefined}
                    onRemove={onRemoveProductManifestItem}
                    onPress={onPressProductManifestItem}
                  />
                ))}
            </StackLayout>
          )}

        {/** medias */}
        <ItemMediaList
          editable
          medias={item.medias ?? []}
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          required={selectedOption?.isPicPreferred}
          onTakeMedia={handleTakeMedia}
          onDeleteMedia={handleDeleteMedia}
          onEditMedia={handleEditMedia}
          onPreviewVideo={handlePreviewVideo}
          onRetryMediaUpload={handleRetryMediaUpload}
        />
      </View>
    );
  },
);
